// ─────────────────────────────────────────────────────────────────────────────
// notificationsService.js — Enterprise notification centre API.
//
//   REST (via the shared api client, header-authenticated):
//     GET   /notifications            → { notifications, unreadCount }
//     PATCH /notifications/:id/read   → mark one read
//     POST  /notifications/read-all   → mark all read
//
//   SSE (native EventSource, query-token authenticated):
//     GET   /notifications/stream?token=…   → live push of new notifications
//
// The SSE half can't ride the api.js fetch wrapper (EventSource has no way to
// set an Authorization header and no access to the refresh-on-401 flow), so
// connectNotificationStream() owns the raw connection, reconnection and auth.
// ─────────────────────────────────────────────────────────────────────────────
import api, { BASE_URL, TokenStorage } from "./api";

// ── REST ──────────────────────────────────────────────────────────────────────

/** History + unread count for the bell. */
export async function getNotifications({ limit, unreadOnly } = {}) {
  const qs = new URLSearchParams();
  if (limit != null)      qs.set("limit", String(limit));
  if (unreadOnly)         qs.set("unreadOnly", "true");
  const suffix = qs.toString() ? `?${qs}` : "";
  return api.get(`/notifications${suffix}`);
}

export async function markNotificationRead(id) {
  return api.patch(`/notifications/${id}/read`);
}

export async function markAllNotificationsRead() {
  return api.post("/notifications/read-all");
}

// ── SSE ─────────────────────────────────────────────────────────────────────
// Reconnection strategy (best-practice notes):
//   • We disable EventSource's built-in auto-reconnect (by close()-ing on error)
//     and drive our own capped exponential backoff. This lets us (a) re-read a
//     possibly-refreshed access token on each attempt and (b) avoid a tight
//     reconnect loop hammering the server when the token is dead.
//   • The backend assigns no event IDs and does not replay missed events, so
//     there's no Last-Event-ID resume. Instead the caller refetches history on
//     every (re)open (onOpen) to close any gap accrued while disconnected.
//   • The access token is passed as a query param because EventSource cannot
//     send headers. It is short-lived; if it expires the connection errors and
//     we retry — a parallel REST call refreshes npq_token, which the next
//     attempt picks up.

const RECONNECT_BASE_MS = 1_000;   // first retry delay
const RECONNECT_MAX_MS  = 30_000;  // ceiling for the backoff

/**
 * Open a managed SSE connection to the notification stream.
 *
 * @param {{
 *   onNotification: (n: object) => void,  // a new notification arrived
 *   onOpen?: () => void,                  // (re)connected — caller should refetch to fill gaps
 *   onError?: () => void,                 // connection dropped (will retry)
 * }} handlers
 * @returns {() => void} stop — closes the connection and cancels any pending retry.
 */
export function connectNotificationStream(handlers) {
  let es          = null;
  let retryTimer  = null;
  let attempts    = 0;
  let stopped     = false;

  function scheduleReconnect() {
    if (stopped) return;
    // Exponential backoff, capped: 1s, 2s, 4s … 30s.
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempts, RECONNECT_MAX_MS);
    attempts += 1;
    retryTimer = setTimeout(connect, delay);
  }

  function connect() {
    if (stopped) return;

    const token = TokenStorage.get();
    if (!token) {
      // Not authenticated (yet) — retry later; a login will populate the token.
      scheduleReconnect();
      return;
    }

    const url = `${BASE_URL}/notifications/stream?token=${encodeURIComponent(token)}`;
    es = new EventSource(url);

    es.onopen = () => {
      attempts = 0;              // reset backoff on a healthy connection
      handlers.onOpen?.();
    };

    es.onmessage = (e) => {
      let payload;
      try { payload = JSON.parse(e.data); } catch { return; } // ignore keepalives / malformed
      handlers.onNotification?.(payload);
    };

    es.onerror = () => {
      // EventSource fires onerror on any drop. We take over reconnection so we
      // control the backoff and re-read the token, so tear this socket down.
      handlers.onError?.();
      if (es) { es.close(); es = null; }
      scheduleReconnect();
    };
  }

  connect();

  return function stop() {
    stopped = true;
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
    if (es) { es.close(); es = null; }
  };
}
